import { useRef } from 'react';
import { motion, useMotionValue, useScroll, useSpring, useTransform } from 'framer-motion';
import { TypeAnimation } from 'react-type-animation';
import { useLanguage } from '../LanguageContext';
import './Home.css';

const nameLine = (text) => text.split('');

const fadeUp = (delay) => ({
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] } },
});

const nameContainer = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.035, delayChildren: 0.15 },
  },
};

const letter = {
  hidden: { y: '110%', opacity: 0 },
  show: {
    y: '0%',
    opacity: 1,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  },
};

function Home({ ready }) {
  const { t, lang } = useLanguage();
  const heroRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const smoothX = useSpring(mouseX, { stiffness: 60, damping: 20, mass: 0.6 });
  const smoothY = useSpring(mouseY, { stiffness: 60, damping: 20, mass: 0.6 });

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const scrollShapesSlow = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const scrollShapesFast = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  const handleMouseMove = (e) => {
    const rect = heroRef.current.getBoundingClientRect();
    const relX = (e.clientX - rect.left) / rect.width - 0.5;
    const relY = (e.clientY - rect.top) / rect.height - 0.5;
    mouseX.set(relX);
    mouseY.set(relY);
  };

  const shapeA = {
    x: useTransform(smoothX, (v) => v * 30),
    y: useTransform([smoothY, scrollShapesSlow], ([v, s]) => v * 30 + s),
  };
  const shapeB = {
    x: useTransform(smoothX, (v) => v * -45),
    y: useTransform([smoothY, scrollShapesFast], ([v, s]) => v * -45 + s),
  };
  const shapeC = {
    x: useTransform(smoothX, (v) => v * 60),
    y: useTransform([smoothY, scrollShapesSlow], ([v, s]) => v * 60 + s),
  };
  const shapeD = {
    x: useTransform(smoothX, (v) => v * -25),
    y: useTransform([smoothY, scrollShapesFast], ([v, s]) => v * -25 + s),
  };
  const shapeE = {
    x: useTransform(smoothX, (v) => v * 40),
    y: useTransform([smoothY, scrollShapesSlow], ([v, s]) => v * 40 + s),
  };


  return (
    <section className="hero" ref={heroRef} onMouseMove={handleMouseMove}>
      <div className="hero-shapes" aria-hidden="true">
        <div className="shape-float shape-float-1">
          <motion.div className="shape shape-ring" style={shapeA} />
        </div>
        <div className="shape-float shape-float-2">
          <motion.div className="shape shape-square" style={shapeB} />
        </div>
        <div className="shape-float shape-float-3">
          <motion.div className="shape shape-hex" style={shapeC} />
        </div>
        <div className="shape-float shape-float-4">
          <motion.div className="shape shape-cross">
            <motion.span style={shapeD} />
          </motion.div>
        </div>
        <div className="shape-float shape-float-5">
          <motion.div className="shape shape-blob" style={shapeE} />
        </div>
        <div className="shape-float shape-float-2">
          <motion.div className="shape shape-line" style={shapeB} />
        </div>
      </div>

      <motion.div
        className="hero-content"
        style={{ opacity: contentOpacity, y: contentY }}
        initial="hidden"
        animate={ready ? 'show' : 'hidden'}
        variants={{ hidden: {}, show: {} }}
      >
        <motion.span className="hero-kicker" variants={fadeUp(0.1)}>
          {t.hero.kicker}
        </motion.span>

        <h1 className="hero-name" aria-label="Santhosh Dayakar">
          <span className="hero-name-row">
            <motion.span className="hero-name-word" variants={nameContainer}>
              {nameLine('SANTHOSH').map((ch, i) => (
                <motion.span className="hero-letter" key={i} variants={letter}>
                  {ch}
                </motion.span>
              ))}
            </motion.span>
          </span>
          <span className="hero-name-row">
            <motion.span className="hero-name-word" variants={nameContainer}>
              {nameLine('DAYAKAR').map((ch, i) => (
                <motion.span className="hero-letter" key={i} variants={letter}>
                  {ch}
                </motion.span>
              ))}
            </motion.span>
          </span>
        </h1>

        <motion.div className="hero-role" variants={fadeUp(0.9)}>
          <span className="hero-role-prefix">{t.hero.buildFor}</span>
          <TypeAnimation
            key={lang}
            sequence={t.hero.rotating.flatMap((word) => [word, 2000])}
            wrapper="span"
            speed={50}
            repeat={Infinity}
            className="hero-role-value"
          />
        </motion.div>

        <motion.div className="hero-actions" variants={fadeUp(1.05)}>
          <a href="#projects" className="hero-btn hero-btn-primary">
            {t.hero.viewWork}
          </a>
          <a href="#contact" className="hero-btn hero-btn-ghost glass">
            {t.hero.getInTouch}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        className="hero-scroll-cue"
        initial={{ opacity: 0 }}
        animate={{ opacity: ready ? 1 : 0 }}
        transition={{ duration: 0.8, delay: 1.6 }}
      >
        <span className="hero-scroll-line" />
        <span className="hero-scroll-text">{t.hero.scroll}</span>
      </motion.div>
    </section>
  );
}

export default Home;
